import type { BuyerSubmissionSummaryDto } from "@/api/buyers";
import type { SubTableColumn } from "@/design-system/components/ui";
import { listingNameOrFallback } from "@/lib/listingDisplay";
import { useDateFormatter } from "@/utils/datetime";

import { combinedIntroCopy as copy } from "./combinedIntroCopy";
import type { CombinedIntroSelection } from "./combinedIntroSelection";

/**
 * The tick column on a BUYER's Submissions tab — for a combined intro email and
 * for the bulk bar's ARCHIVE / DELETE. A listing's own "Buyers presented" table
 * shares the table component and never gets this column: an intro email goes to
 * one buyer, and that table is one listing across many buyers.
 *
 * <p>🔴 <b>Every row's box is enabled (D3a, 2026-10-14).</b> The rows the
 * combined intro cannot include (an end-state status, or archived) are exactly
 * the ones staff most want to archive, so eligibility moved to each action: the
 * composer lists such ticks as "left out" and drafts the rest; Archive and
 * Delete apply their own server-computed eligibility the same way.</p>
 */
export function combinedIntroSelectColumn(
  selection: CombinedIntroSelection,
  /** Where the reader is now — the ordering and page on screen. */
  pickedOn: string,
): SubTableColumn<BuyerSubmissionSummaryDto> {
  return {
    key: "combined-intro-select",
    header: <span className="sr-only">{copy.selectColumnHeader}</span>,
    render: (s) => {
      const name = listingNameOrFallback(s.listingDisplayName, s.listingBusinessName);
      return (
        <input
          type="checkbox"
          checked={selection.isSelected(s.id)}
          onChange={() => selection.toggle(s, pickedOn)}
          aria-label={copy.selectAriaLabel(name)}
          data-testid={`combined-intro-select-${s.id}`}
        />
      );
    },
  };
}

/**
 * "Intro sent {date}" under a listing's name, when an intro email about it has
 * gone to the buyer — single or combined (ruling D6). Information, never a
 * block: a listing already introduced can be introduced again.
 *
 * <p>The stamp is an INSTANT, so it goes through the viewer's own formatter
 * rather than the wall-calendar one.</p>
 */
export function IntroSentCaption({ at }: { at: string }) {
  const { date } = useDateFormatter();
  return (
    <span className="block text-caption text-gray-mid" data-testid="combined-intro-sent-caption">
      {copy.introSent(date(at))}
    </span>
  );
}
